import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Receipt,
  ShoppingCart,
  ArrowLeftRight,
  ArrowUpRight,
  ArrowDownLeft,
  UtensilsCrossed,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import type { RecentTransaction } from '../../hooks/useRecentTransactions';

interface RecentTransactionsProps {
  data: RecentTransaction[] | undefined;
  total?: number;
  isLoading?: boolean;
  isRefetching?: boolean;
  onRefresh?: () => void;
  className?: string;
}

const typeConfig: Record<
  RecentTransaction['typeName'],
  { icon: typeof Receipt; color: string; bgColor: string }
> = {
  sale: {
    icon: Receipt,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
  },
  purchase: {
    icon: ShoppingCart,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
  },
  transfer: {
    icon: ArrowLeftRight,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20',
  },
  issue: {
    icon: ArrowUpRight,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20',
  },
  issue_return: {
    icon: ArrowDownLeft,
    color: 'text-teal-600 dark:text-teal-400',
    bgColor: 'bg-teal-50 dark:bg-teal-900/20',
  },
  purchase_return: {
    icon: ArrowDownLeft,
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-900/20',
  },
  consumption: {
    icon: UtensilsCrossed,
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
  },
};

function RecentTransactionsComponent({
  data,
  total = 0,
  isLoading = false,
  isRefetching = false,
  onRefresh,
  className,
}: RecentTransactionsProps) {
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const isRTL = i18n.dir() === 'rtl';
  const ChevronIcon = isRTL ? ChevronLeft : ChevronRight;

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('common:time.justNow');
    if (diffMins < 60) return t('common:time.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('common:time.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('common:time.daysAgo', { count: diffDays });

    return date.toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div
      className={cn(
        'bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 shadow-sm flex flex-col',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary-50 dark:bg-primary-900/20 p-2">
            <Receipt className="h-5 w-5 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">
              {t('transactions.recent')}
            </h3>
            {!isLoading && total > 0 && (
              <p className="text-sm text-secondary-500 dark:text-secondary-400">
                {t('transactions.totalToday', { count: total })}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={isRefetching}
              className={cn(
                'p-2 rounded-lg text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100',
                'dark:text-secondary-400 dark:hover:text-secondary-200 dark:hover:bg-secondary-700',
                'transition-colors disabled:opacity-50'
              )}
              aria-label={t('common:refresh')}
            >
              <RefreshCw
                className={cn('h-4 w-4', isRefetching && 'animate-spin')}
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 rounded-lg bg-secondary-50 dark:bg-secondary-700/50 animate-pulse"
              >
                <div className="w-10 h-10 rounded-lg bg-secondary-200 dark:bg-secondary-600" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-secondary-200 dark:bg-secondary-600 rounded w-1/2" />
                  <div className="h-3 bg-secondary-200 dark:bg-secondary-600 rounded w-3/4" />
                </div>
                <div className="w-20 h-5 bg-secondary-200 dark:bg-secondary-600 rounded" />
              </div>
            ))}
          </div>
        ) : data && data.length > 0 ? (
          <ul className="divide-y divide-secondary-100 dark:divide-secondary-700" role="list">
            {data.map((transaction) => {
              const config = typeConfig[transaction.typeName];
              const Icon = config.icon;

              return (
                <li key={transaction.id}>
                  <Link
                    to={`/invoices/${transaction.typeName}/${transaction.invoiceNo}`}
                    className={cn(
                      'flex items-center gap-3 px-6 py-3 hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors'
                    )}
                  >
                    <div className={cn('rounded-lg p-2', config.bgColor)}>
                      <Icon className={cn('h-5 w-5', config.color)} aria-hidden="true" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
                          {transaction.invoiceNo}
                        </p>
                        <span
                          className={cn(
                            'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                            config.bgColor,
                            config.color
                          )}
                        >
                          {t(`common:invoice.types.${transaction.typeName}`)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                          {transaction.outletName} - {transaction.itemCount}{' '}
                          {t('common:items', { count: transaction.itemCount })}
                        </p>
                        <span className="text-secondary-300 dark:text-secondary-600">|</span>
                        <span className="text-xs text-secondary-400 dark:text-secondary-500 whitespace-nowrap">
                          {formatTimeAgo(transaction.createdAt)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                          {formatCurrency(transaction.amount)}
                        </p>
                        <div className="flex items-center justify-end gap-1">
                          {transaction.posted ? (
                            <>
                              <CheckCircle
                                className="h-3 w-3 text-green-500"
                                aria-hidden="true"
                              />
                              <span className="text-xs text-green-600 dark:text-green-400">
                                {t('common:status.posted')}
                              </span>
                            </>
                          ) : (
                            <>
                              <Clock
                                className="h-3 w-3 text-yellow-500"
                                aria-hidden="true"
                              />
                              <span className="text-xs text-yellow-600 dark:text-yellow-400">
                                {t('common:status.pending')}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <ChevronIcon
                        className="h-4 w-4 text-secondary-400 dark:text-secondary-500"
                        aria-hidden="true"
                      />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
            <div className="rounded-full bg-secondary-100 dark:bg-secondary-700 p-3 mb-3">
              <Receipt
                className="h-8 w-8 text-secondary-400 dark:text-secondary-500"
                aria-hidden="true"
              />
            </div>
            <p className="text-secondary-600 dark:text-secondary-400 font-medium">
              {t('transactions.noTransactions')}
            </p>
            <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
              {t('transactions.noTransactionsDescription')}
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      {data && data.length > 0 && (
        <div className="px-6 py-3 border-t border-secondary-200 dark:border-secondary-700">
          <Link
            to="/invoices"
            className={cn(
              'flex items-center justify-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700',
              'dark:text-primary-400 dark:hover:text-primary-300 transition-colors'
            )}
          >
            {t('transactions.viewAll')}
            <ChevronIcon className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      )}
    </div>
  );
}

export const RecentTransactions = memo(RecentTransactionsComponent);
export default RecentTransactions;
